import { FormattedDate } from '@app/format-date';
import { ContentCopyIcon } from '@app/ui/icons/material/ContentCopy';
import React, { useState, useRef, useEffect } from 'react';
import { FileText, Eye, Download, Archive, Send, X, ChevronDown, Loader2 } from 'lucide-react';
import { MintDocDialog } from '../dialogs/doc-mint-modal';
import { useDriveStore } from '@app/drive/drive-store'; // Changed from driveState to useDriveStore

export const docTypeMap: Record<string, string> = {
    T: 'Trust',
    W: 'Will',
    D: 'Deed',
    P: 'PFS',
    A: 'Advanced',
    R: 'POA',
};

export function DocumentsTable({ fileEntries }: any) {
    const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
    const [currentDocUrl, setCurrentDocUrl] = useState<string>('');
    const [currentDocName, setCurrentDocName] = useState<string>('');
    const [isDownloading, setIsDownloading] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    
    // Get the active dialog from store - using reactive subscription
    const activeDialog = useDriveStore(s => s.activeActionDialog);
    const setActiveActionDialog = useDriveStore(s => s.setActiveActionDialog);

    const copyToClipboard = (tokenId: string) => {
        navigator.clipboard.writeText(tokenId);
        setCopiedTokenId(tokenId);
        setTimeout(() => setCopiedTokenId(null), 1500);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (openDropdown) {
                const dropdownElement = dropdownRefs.current[openDropdown];
                if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
                    setOpenDropdown(null);
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdown]);

    const handleView = (docUrl: string, docName: string) => {
        setCurrentDocUrl(docUrl);
        setCurrentDocName(docName);
        setIsLoading(true);
        setViewModalOpen(true);
        setOpenDropdown(null);
    };

    const handleDownload = async (docUrl: string, fileName: string, entryId: string) => {
        setIsDownloading(entryId);
        try {
            const response = await fetch(docUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Extract file extension from URL or use default
            const urlExtension = docUrl.split('.').pop()?.split('?')[0];
            const fileExtension = urlExtension || 'pdf';
            const sanitizedFileName = fileName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
            link.download = `${sanitizedFileName}.${fileExtension}`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setOpenDropdown(null);
        } catch (error) {
            console.error('Download failed:', error);
            alert('Download failed. Please check your connection and try again.');
        } finally {
            setIsDownloading(null);
        }
    };

    const handleStoreForever = (entry: any) => {
        // Open the Mint Form modal for "Store Forever"
        console.log('Store Forever clicked for:', entry.name);
        setActiveActionDialog('mint', [entry]);
        setOpenDropdown(null);
    };

    const handleSend = (entry: any) => {
        // Add your send logic here
        console.log('Send clicked for:', entry.name);
        // Example: Open send modal or call send API
        // await sendDocumentAPI(entry.id);
        setOpenDropdown(null);
    };

    const ActionDropdown = ({ entry }: { entry: any }) => {
        const isOpen = openDropdown === entry.id;
        const isCurrentlyDownloading = isDownloading === entry.id;

        return (
            <div
                className="inline-block text-left"
                ref={(el) => {
                    dropdownRefs.current[entry.id] = el;
                }}
            >
                <button
                    onClick={() => setOpenDropdown(isOpen ? null : entry.id)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200"
                >
                    <span className="mr-2">⚡</span>
                    Actions
                    <ChevronDown
                        className={`ml-2 h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
                            }`}
                    />
                </button>

                {isOpen && (
                    <div className="absolute right-40 z-10 mt-2 rounded-xl shadow-2xl bg-white ring-1 ring-black ring-opacity-5 backdrop-blur-sm border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="py-10 px-10" role="menu">
                            <button
                                onClick={() => handleView(entry.docurl, entry.name)}
                                className="flex items-center w-full px-5 py-5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:text-blue-700 transition-all duration-150 group"
                                role="menuitem"
                            >
                                <div className="flex items-center justify-center w-20 h-20 rounded-lg bg-blue-100 group-hover:bg-blue-200 transition-colors duration-150 mr-3 flex-shrink-0">
                                    <Eye className="text-blue-600" />
                                </div>
                                <span className="font-medium whitespace-nowrap">View Document</span>
                            </button>

                            <button
                                onClick={() => handleDownload(entry.docurl, entry.name, entry.id)}
                                disabled={isCurrentlyDownloading}
                                className="flex items-center w-full px-5 py-5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-green-100 hover:text-green-700 transition-all duration-150 group disabled:opacity-50 disabled:cursor-not-allowed"
                                role="menuitem"
                            >
                                <div className="flex items-center justify-center w-20 h-20 rounded-lg bg-green-100 group-hover:bg-green-200 transition-colors duration-150 mr-3 flex-shrink-0">
                                    {isCurrentlyDownloading ? (
                                        <Loader2 className=" text-green-600 animate-spin" />
                                    ) : (
                                        <Download className="text-green-600" />
                                    )}
                                </div>
                                <span className="font-medium whitespace-nowrap">
                                    {isCurrentlyDownloading ? 'Downloading...' : 'Download File'}
                                </span>
                            </button>

                            <button
                                onClick={() => handleStoreForever(entry)}
                                className="flex items-center w-full px-5 py-5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-purple-100 hover:text-purple-700 transition-all duration-150 group"
                                role="menuitem"
                            >
                                <div className="flex items-center justify-center w-20 h-20 rounded-lg bg-purple-100 group-hover:bg-purple-200 transition-colors duration-150 mr-3 flex-shrink-0">
                                    <Archive className="text-purple-600" />
                                </div>
                                <span className="font-medium whitespace-nowrap">Store Forever</span>
                            </button>

                            <button
                                onClick={() => handleSend(entry)}
                                className="flex items-center w-full px-5 py-5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-700 transition-all duration-150 group"
                                role="menuitem"
                            >
                                <div className="flex items-center justify-center w-20 h-20 rounded-lg bg-orange-100 group-hover:bg-orange-200 transition-colors duration-150 mr-3 flex-shrink-0">
                                    <Send className="text-orange-600" />
                                </div>
                                <span className="font-medium whitespace-nowrap">Send Document</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const DocumentModal = () => {
        if (!viewModalOpen) return null;

        const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if (e.target === e.currentTarget) {
                setViewModalOpen(false);
            }
        };

        const handleIframeLoad = () => {
            setIsLoading(false);
        };

        return (
            <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleBackdropClick}>
                <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div
                        className="fixed inset-0 transition-opacity backdrop-blur-sm"
                        aria-hidden="true"
                        onClick={handleBackdropClick}
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 via-blue-900/60 to-purple-900/40"></div>
                    </div>

                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                        &#8203;
                    </span>

                    <div className="inline-block align-bottom bg-white rounded-2xl px-6 pt-6 pb-6 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full border border-gray-100 animate-in fade-in zoom-in-95 duration-300">
                        <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
                            <button
                                type="button"
                                onClick={() => setViewModalOpen(false)}
                                className="mt-10 mr-10 inline-flex items-center justify-center w-20 h-20 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                            >
                                <X className="h-20 w-20" />
                            </button>
                        </div>

                        <div className="sm:flex sm:items-start">
                            <div className="mt-1 text-center sm:mt-0 sm:text-left w-full">
                                <div className="flex items-center mb-6">
                                    <div className="ml-5">
                                        <h3 className="text-2xl leading-7 font-bold text-gray-900">
                                            Document Viewer
                                        </h3>
                                        <p className="text-sm text-gray-500 mt-1">{currentDocName}</p>
                                    </div>
                                </div>
                                <div className="mt-4 w-full">
                                    <div className="w-full h-[700px] bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl flex items-center justify-center shadow-inner border border-gray-200 overflow-hidden relative">
                                        {isLoading && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                                                <div className="text-center">
                                                    <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
                                                    <p className="text-gray-600 font-medium">Loading document...</p>
                                                </div>
                                            </div>
                                        )}
                                        <iframe
                                            src={currentDocUrl}
                                            className="w-full h-full rounded-xl border-0"
                                            title="Document Viewer"
                                            onLoad={handleIframeLoad}
                                            style={{ minHeight: '700px' }}
                                        />
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-between items-center">
                                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                                        <span className="w-2 h-2 rounded-full bg-blue-500 mr-2 animate-pulse"></span>
                                        Document loaded and ready to view
                                    </div>

                                    <button
                                        onClick={() => handleDownload(currentDocUrl, currentDocName, 'modal')}
                                        disabled={isDownloading === 'modal'}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isDownloading === 'modal' ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Downloading...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="h-4 w-4 mr-2" />
                                                Download
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="p-16 text-center">
            <div className="overflow-auto rounded-2xl shadow-lg mb-20">
                <table className="w-full table-auto border-collapse border p-10">
                    <thead className="bg-gray-200">
                        <tr>
                            <th className="border-b px-14 py-10 text-center">Image</th>
                            <th className="border-b px-14 py-10 text-left">#Document Hash Key</th>
                            <th className="border-b px-14 py-10 text-left">Document Name</th>
                            <th className="border-b px-14 py-10 text-center">Author</th>
                            <th className="border-b px-14 py-10 text-center">Document Type</th>
                            <th className="border-b px-14 py-10 text-center">Created At</th>
                            <th className="border-b px-14 py-10 text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fileEntries.length > 0 &&
                            fileEntries.map((entry: any, index: number) => {
                                return (
                                    <tr
                                        key={entry?.id || index}
                                        className={`hover:bg-gray-100 cursor-pointer transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                            } ${index === fileEntries.length - 1 ? 'last:border-b-0' : ''}`}
                                    >
                                        <td className="border-b px-14 py-10 text-center">
                                            {entry?.minted_data?.[0]?.doc_image_path ? (
                                                <img
                                                    src={entry?.minted_data?.[0]?.doc_image_path}
                                                    alt={entry?.name}
                                                    className="h-50 w-50 object-cover rounded-full mx-auto"
                                                />
                                            ) : (
                                                <span className="text-gray-400 flex items-center justify-center">
                                                    <FileText className="w-24 h-24" />
                                                </span>
                                            )}
                                        </td>
                                        <td className="border-b px-4 py-4 text-left">
                                            {Array.isArray(entry.nft_ids) && entry.nft_ids.length > 0 ? (
                                                entry.nft_ids.map((item: any, idx: number) => (
                                                    <div key={item.token_id || idx} className="flex items-center space-x-2 mb-1">
                                                        <span className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                                            {item.token_id}
                                                        </span>
                                                        <button
                                                            className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                copyToClipboard(item.token_id);
                                                            }}
                                                            title="Copy to clipboard"
                                                        >
                                                            {copiedTokenId === item.token_id ? (
                                                                <span className="text-xs text-green-600 font-medium">✓ Copied</span>
                                                            ) : (
                                                                <ContentCopyIcon className="cursor-pointer text-sm" />
                                                            )}
                                                        </button>
                                                    </div>
                                                ))
                                            ) : entry?.disk_prefix ? (
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                                        {entry.disk_prefix}
                                                    </span>
                                                    <button
                                                        className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyToClipboard(entry.disk_prefix);
                                                        }}
                                                        title="Copy to clipboard"
                                                    >
                                                        {copiedTokenId === entry.disk_prefix ? (
                                                            <span className="text-xs text-green-600 font-medium">✓ Copied</span>
                                                        ) : (
                                                            <ContentCopyIcon className="cursor-pointer text-sm" />
                                                        )}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-sm">N/A</span>
                                            )}
                                        </td>
                                        <td className="border-b px-14 py-10 text-left font-medium text-gray-900">
                                            {entry?.name}
                                        </td>
                                        <td className="border-b px-14 py-10 text-center text-gray-700">
                                            {entry?.author}
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full">
                                                {entry?.document_type}
                                            </span>
                                        </td>
                                        <td className="border-b px-14 py-10 text-center text-gray-600">
                                            <FormattedDate date={entry?.created_at} />
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            <ActionDropdown entry={entry} />
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
            <DocumentModal />
            
            {/* Render Mint Dialog when "Store Forever" is clicked */}
            {activeDialog?.name === 'mint' && activeDialog.entries.length > 0 && (
                <MintDocDialog entries={activeDialog.entries} />
            )}
        </div>
    );
}
